// src/components/tool-calls/toolCallView.tsx
// Presentational primitives shared by the chat ToolCallDrawer and the
// query-log detail drawer: JSON/markdown/code blocks, copy buttons, status
// badge, and the small key/value + section scaffolding. Pure display — no
// data fetching, no domain logic. Kept neutral so either surface can import it.

import React, { useState } from 'react';
import { CheckCircle2, XCircle, Copy, Check } from 'lucide-react';
import { clsx } from 'clsx';
import MarkdownMessage from '../chat/MarkdownMessage';

export const CodeBlockSection: React.FC<{
  title: string;
  code: string;
  language?: string;
  variant?: 'error';
}> = ({ title, code, variant }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // silent
    }
  };
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
        <button
          onClick={handleCopy}
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors',
            'text-muted-foreground hover:bg-accent',
            'focus:outline-none focus:ring-2 focus:ring-ring/40',
          )}
          aria-label={`Copy ${title}`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        className={clsx(
          'text-xs font-mono rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words border',
          variant === 'error'
            ? 'text-destructive bg-destructive/15 border-destructive/20'
            : 'text-foreground bg-background border-border',
        )}
      >
        {code}
      </pre>
    </section>
  );
};

export const MarkdownResultSection: React.FC<{
  markdown: string;
  fullPayload: Record<string, unknown>;
  title?: string;
}> = ({ markdown, fullPayload, title = 'Result' }) => {
  const [view, setView] = useState<'markdown' | 'json'>('markdown');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = view === 'markdown' ? markdown : JSON.stringify(fullPayload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; silent failure is fine.
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-md border border-border overflow-hidden text-xs"
            role="tablist"
            aria-label="Result view"
          >
            <ViewToggleButton active={view === 'markdown'} onClick={() => setView('markdown')}>
              Markdown
            </ViewToggleButton>
            <ViewToggleButton active={view === 'json'} onClick={() => setView('json')}>
              JSON
            </ViewToggleButton>
          </div>
          <button
            onClick={handleCopy}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors',
              'text-muted-foreground hover:bg-accent',
              'focus:outline-none focus:ring-2 focus:ring-ring/40',
            )}
            aria-label={`Copy result as ${view}`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      {view === 'markdown' ? (
        <div className="rounded-md p-3 bg-background border border-border overflow-x-auto">
          <MarkdownMessage content={markdown} />
        </div>
      ) : (
        <JsonBlock value={fullPayload} />
      )}
    </section>
  );
};

export const ViewToggleButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={clsx(
      'px-2.5 py-1 font-medium transition-colors focus:outline-none',
      active
        ? 'bg-accent text-foreground'
        : 'text-muted-foreground hover:text-foreground',
    )}
  >
    {children}
  </button>
);

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section>
    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
      {title}
    </h3>
    <div className="space-y-1.5">{children}</div>
  </section>
);

export const KeyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline gap-3 text-sm">
    <span className="w-24 flex-shrink-0 text-muted-foreground">{label}</span>
    <span className="text-foreground break-words">{value}</span>
  </div>
);

export const JsonSection: React.FC<{ title: string; value: unknown }> = ({ title, value }) => (
  <section>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </h3>
      {value != null && <CopyButton value={value} ariaLabel={`Copy ${title.toLowerCase()}`} />}
    </div>
    <JsonBlock value={value} />
  </section>
);

export const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="text-xs font-mono text-foreground bg-background rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words border border-border">
    {value != null ? JSON.stringify(value, null, 2) : '—'}
  </pre>
);

export const CopyButton: React.FC<{ value: unknown; ariaLabel: string }> = ({
  value,
  ariaLabel,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; silent failure is fine.
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors',
        'text-muted-foreground hover:bg-accent',
        'focus:outline-none focus:ring-2 focus:ring-ring/40'
      )}
      aria-label={ariaLabel}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

export const StatusBadge: React.FC<{ status: 'success' | 'failed' }> = ({ status }) => {
  const isSuccess = status === 'success';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
        isSuccess
          ? 'bg-success/15 text-success'
          : 'bg-destructive/15 text-destructive'
      )}
    >
      {isSuccess ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {isSuccess ? 'Success' : 'Failed'}
    </span>
  );
};

